import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { xor } from 'lodash';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import { useMemo } from 'react';
import { useProductEnhancementDocumentProvider } from '../../ProductEnhancementDocument.Hooks';
import { useProductEnhancementDealerDocumentProvider } from '../ProductEnhancementDocument.DealerDocuments.Hooks';

export interface ProductEnhancementDealerDocumentAddForm {
  formFile: File;
  dealers: number[];
  productEnhancementId: number;
  productEnhancementDocumentId: number;
}

export default function ProductEnhancementDealerDocumentAdd() {
  const { lang } = getLocales();
  const { productEnhancementDocument } = useProductEnhancementDocumentProvider();
  const { setView, fullDealerDocumentList, handleUploadDealerDocumentSubmit } =
    useProductEnhancementDealerDocumentProvider();

  const dealerListApi = useApi({
    action: Dealer.list,
    body: {
      isInactive: false,
      productEnhancementId: productEnhancementDocument?.productEnhancementId
        ? [productEnhancementDocument.productEnhancementId]
        : null,
    },
  });

  const availableDealers = useMemo<Dealer[]>(() => {
    const dealerData = dealerListApi.payload?.data;
    if (!dealerData?.length) {
      return [];
    }

    if (fullDealerDocumentList?.length > 0) {
      return dealerData.filter((dealer) => !fullDealerDocumentList.some((doc) => doc.dealerId === dealer.id));
    }

    return dealerData;
  }, [dealerListApi.payload?.data, fullDealerDocumentList]);

  return (
    <ModalForm<ProductEnhancementDealerDocumentAddForm>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[productEnhancementDocument]}
      title={lang.upload}
      default={{
        productEnhancementDocumentId: productEnhancementDocument?.id,
        productEnhancementId: productEnhancementDocument?.productEnhancementId,
      }}
      onCancel={() => setView(undefined)}
      onSubmit={(data) => handleUploadDealerDocumentSubmit(data)}
      submitButtonText={lang.upload}
      render={({ form }) => (
        <>
          <FormWrapper>
            <FileInput
              name="file"
              fileLimit={1}
              onChange={async ({ file }) => {
                const formFile = file && file[0]?.original;
                form.update({ formFile });
              }}
              class={ThemeFileInputDropzone}
              error={form.errors.formFile?._objectError}
              value={
                form.data.formFile
                  ? [
                      {
                        original: form.data.formFile,
                        type: form.data.formFile?.type as Utils.File.MimeType,
                        dataUrl: window.URL.createObjectURL(form.data.formFile),
                      },
                    ]
                  : undefined
              }
            />
          </FormWrapper>
          <Box style={{ display: 'flex', justifyContent: 'flex-end', padding: Theme.Size.S }}>
            <Button
              class={{ button: ThemeButtonCircle }}
              media={form.data.dealers?.length === availableDealers?.length ? 'fas-minus-square' : 'fas-check-square'}
              onClick={form.update}
              data={{
                dealers:
                  !availableDealers || form.data.dealers?.length === availableDealers?.length
                    ? []
                    : [...availableDealers.map((x) => x.id)],
              }}
              active={form.data.dealers?.length === availableDealers?.length}
              htmlElementProps={{ title: lang.selectAll }}
              render={lang.selectAll}
            />
          </Box>
          <table className={ThemeTableSimple}>
            <thead>
              <tr>
                <th children={lang.dealer} />
                <th children={lang.actions} style={{ textAlign: 'center' }} />
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={availableDealers}
                render={({ data }) => {
                  const isItemSelected = form.data.dealers?.find((it) => it === data.id);

                  return (
                    <tr key={data.id}>
                      <td children={data.displayInfo.title} />
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          data={data}
                          onClick={() => {
                            form.update({ dealers: xor(form.data.dealers, [data.id]) });
                          }}
                          class={ThemeButtonCircle}
                          media={<Icon class={isItemSelected ? 'fas-check-square' : 'far-square'} />}
                          htmlElementProps={{ title: isItemSelected ? lang.unselect : lang.select }}
                        />
                      </td>
                    </tr>
                  );
                }}
              />
            </tbody>
          </table>
        </>
      )}
    />
  );
}
